// Opciones de los selects del ABM de agentes.
//
// Los catálogos que comparte con otra vista o con una columna de la grilla viven en
// `consts/` y se reexportan desde acá. Ver el README del módulo.

// Los códigos son los que espera UsuarioBasicosGet en `usuarioSexo`.
export { GENEROS } from "consts/generos";

export { LEYES } from "consts/leyes";

// ESTADOS_OPERATIVO son los cuatro e incluye Licencia; ESTADOS_CONEXION, los tres del
// vínculo. No son intercambiables.
export {
    ESTADOS_AGENTE,
    ESTADOS_CONEXION,
    ESTADOS_OPERATIVO,
    etiquetaEstado,
} from "consts/estados-agente";

export { ASIGNACIONES } from "consts/asignaciones";

/**
 * Tipo de horario del agente: si su jornada es siempre la misma o va rotando.
 *
 * Se puede NO asignar, y por eso el select tiene su opción vacía: el back acepta `null`
 * además de F y R, y un agente recién creado todavía no lo tiene definido.
 *
 * Ojo que NO es `rnhHorarioTipo`, que es un campo del catálogo de horarios y no tiene
 * relación con este.
 */
export const TIPOS_HORARIO = [
    { cod: "F", label: "Fijo" },
    { cod: "R", label: "Rotativo" },
];

/**
 * La etiqueta del tipo, para la grilla del detalle.
 *
 * Un código que no esté en la tabla devuelve el CRUDO y no "Sin asignar", por lo mismo
 * que `etiquetaUnico`: un valor inesperado tiene que VERSE —raro pero visible— en vez
 * de disfrazarse de campo sin cargar.
 */
export const etiquetaTipoHorario = (cod) =>
    TIPOS_HORARIO.find((t) => t.cod === cod)?.label ?? (cod || "Sin asignar");

/**
 * Largo de los campos propios del agente.
 *
 * PENDIENTE: confirmar con el back el largo real de legajo y matrícula. El 20 es una
 * elección nuestra —entra cualquier formato conocido, "MP 12345" o un legajo numérico—
 * pero no es un dato que nos hayan dado.
 */
export const LARGO_AGENTE = {
    legajo: 20,
    matricula: 20,
    email: 100,
    // Los dos del vínculo. 50 es el límite que declara el contrato de Ins y Upd; no
    // sale de `LARGO.nombre`, que vale 45 y rechazaría apellidos que el back acepta.
    apellidos: 50,
    nombres: 50,
};

// Si la asignación única/múltiple ya se puede GUARDAR. Desde que RNHAgenteUpd acepta
// `rnhAgenteEstablecimientoUnico`, sí. Queda la constante porque todavía no se probó
// contra el back: si el param no entra, se vuelve a `false` y el aviso reaparece solo.
export const PUEDE_GUARDAR_ASIGNACION = true;

/**
 * Un agente DE BAJA queda de solo lectura. Alcanza con que CUALQUIERA de sus dos
 * estados esté en "B".
 *
 * Recibe los códigos sueltos y no el agente entero porque cada vista los tiene con un
 * nombre distinto: la fila del listado los mapea y el Get por id los trae crudos.
 */
export const estaDeBaja = (...statusCods) => statusCods.includes("B");

/**
 * La matrícula es binaria: el título la exige o no la admite, y lo define
 * `rnhTituloRequiereMatricula`. Sin título elegido todavía no hay nada que decidir.
 */
/**
 * Lo que se muestra —y se manda— en matrícula cuando el título no la admite.
 *
 * Se MUESTRA siempre que el campo esté deshabilitado, pero solo VIAJA si hay un título
 * elegido: el contrato rechaza una matrícula sin título (41021), así que mandarla en un
 * alta sin título —el caso más común— haría fallar el alta entera.
 */
export const MATRICULA_NO_APLICA = "N/A";

/**
 * El "Sin Cargo" del catálogo. NO es lo mismo que no mandar cargo: es un cargo real,
 * con su cod, que el back guarda como tal y que el organigrama usa para no restringir
 * el tipo de componente.
 *
 * Se busca por nombre porque el cod lo define el ABM de cargos y puede cambiar. Si
 * algún día deja de existir, el select simplemente abre sin preselección.
 */
export const esSinCargo = (cargo) =>
    /^\s*sin\s+cargo\s*$/i.test(cargo?.rnhCargoNombre ?? "");

export const requiereMatricula = (titulos, tituloCod) => {
    if (!tituloCod) return false;
    const titulo = (titulos ?? []).find(
        (t) => String(t.rnhTituloCod) === String(tituloCod)
    );
    return titulo?.rnhTituloRequiereMatricula === "Y";
};
